import { ElementRef, inject, Injectable, Injector, Type } from '@angular/core';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { normalizeProducts } from '@df/catalog-ui/related-products/normalize-products.fn';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import difference from 'lodash-es/difference';
import uniq from 'lodash-es/uniq';
import { filter, Observable, Subject, Subscription } from 'rxjs';
import { NCatalogUi } from '../df-catalog-ui.config';

export interface IRelatedProductsOptions {
    /**
     * Engine to use, be default only native related are used
     * See registerEngine and _engines for engines
     * @type {string}
     */
    engines: string | (string | IEngineRequest)[];
    /**
     * Normalize after load (removes out of stock and skips non divargend doubles)
     * @type {Boolean}
     */
    normalize?: boolean;
    /**
     * Skip discounted products
     * @type {Boolean}
     */
    disalowDiscounted?: boolean;
    /**
     * Allow non routable products in results
     * @type {boolean}
     */
    nonRoutable?: boolean;
}

export interface IRelatedProductsEngineHandler<T extends Product> {
    elementRef: ElementRef;
    $listing: Observable<ProductListing<T> | undefined>;
    forceUpdate?: boolean;
}

export interface IRelatedProductsOptionsExtended<T extends Product> extends IRelatedProductsOptions {
    component: IRelatedProductsEngineHandler<T>;
}

export interface IEngineRequest {
    id: string;
    options?: any;
}

export type IRelatedProductsCallback = (relatedIds: number[]) => any | void;

/**
 * Engine to generate related products ids
 */
export interface IRelatedProductsEngine<T extends Product = Product> {
    get: (
        ids: number[],
        callback: IRelatedProductsCallback,
        engineOptions: any | undefined,
        options: IRelatedProductsOptionsExtended<T>
    ) => void;

    subscribe?: (cb: () => void) => Subscription;

    /**
     * Pass true to disable caching results
     */
    noCache?: boolean;
}

export interface IRelatedProductsEngineInstance<TItem extends Product, TEngine extends IRelatedProductsEngine<TItem>> {
    new (): TEngine;
}

@Injectable({
    providedIn: 'root'
})
export class RelatedProductsService {
    /**
     * Listing sort engines
     */
    static engines: {
        [id: string]: { type: IRelatedProductsEngineInstance<Product, IRelatedProductsEngine>; instance?: IRelatedProductsEngine };
    } = {};

    /**
     * Max number of products we may try to use
     */
    static maxRelatedItems = 100;

    static registerEngine(id: string, type: Type<IRelatedProductsEngine>) {
        RelatedProductsService.engines[id] = {
            type
        };
    }

    private moduleConfig = inject(NCatalogUi.OPTIONS);

    private logger = inject(Logger);
    private app = inject(App);
    private injector = inject(Injector);

    // engines subscriptions
    private subscriptions = new Subscription();
    private engineUpdate = new Subject<string>();

    /**
     * Postprocessing of related ids to filter out unwanted and apply limites
     */
    async postprocess(sourceIds: number[], relatedIds: number[], options: IRelatedProductsOptions, cb: IRelatedProductsCallback) {
        // make sure no empty values there
        relatedIds = uniq(relatedIds.filter(i => !!i));
        if (relatedIds.length) {
            await this.app.resources.product.loadAllIds(relatedIds, 2, { safe: true });
            if (options.normalize) {
                relatedIds = this.normalize(sourceIds, relatedIds);
            }
            if (options.disalowDiscounted) {
                relatedIds = this.skipDiscounted(relatedIds);
            }

            cb(relatedIds);
        } else {
            cb([]);
        }
    }

    /**
     * Retrieves related products ids matching requested product model
     * Function is asynchronous and requires callback
     *
     * @param  {any}   product ids, products collection or product model (see resource.toIds to see normalization rules)
     * @param  {object}   See defaultOptions for possible options
     * @param  {(err, ids) => void} callback
     *
     */
    get(
        sourceProducts: number | number[] | Product | Product[],
        component: IRelatedProductsEngineHandler<Product>,
        _options: IRelatedProductsOptions,
        cb: IRelatedProductsCallback
    ): void {
        // prepare options
        const options = <IRelatedProductsOptions>Object.assign({}, this.moduleConfig.relatedProducts?.defaultConfig || {}, _options);

        // normalize input
        const sourceProductIds = this.app.resources.product.toIds(sourceProducts);

        const key = [sourceProductIds, _options];

        const cacheRes = this.app.cache.get(key);

        if (cacheRes && !component.forceUpdate) {
            cb(cacheRes);
        } else {
            const engines = (typeof options.engines === 'string' ? options.engines.split(',') : options.engines).map(i =>
                typeof i === 'string' ? { id: i } : i
            );
            const allRelatedIds: number[] = [];
            let cacheAllowed = true;
            const finalEngineCallback = () =>
                this.postprocess(sourceProductIds, allRelatedIds, options, ids => {
                    if (cacheAllowed) {
                        this.app.cache.set(key, ids);
                    }
                    cb(ids);
                });

            const runEngine = (engineIndex: number) => {
                if (engineIndex >= engines.length || allRelatedIds.length >= RelatedProductsService.maxRelatedItems) {
                    finalEngineCallback();
                } else {
                    const engineRequest = engines[engineIndex];
                    const engine = engineRequest ? this.getEngine(engineRequest.id) : undefined;
                    if (!engine) {
                        this.logger.error('Invalid related products engine:', engineRequest.id);
                        runEngine(engineIndex + 1);
                    } else {
                        if (engine.noCache) {
                            cacheAllowed = false;
                        }
                        engine.get(
                            sourceProductIds,
                            (relatedIds: number[]) => {
                                allRelatedIds.push(...difference(relatedIds, allRelatedIds));
                                runEngine(engineIndex + 1);
                            },
                            engineRequest.options,
                            { ...options, component }
                        );
                    }
                }
            };

            runEngine(0);
        }
    }

    /**
     * Subscribe to engine updates
     */
    subscribe(options: IRelatedProductsOptions, cb: () => void): Subscription {
        const enginesIds = (typeof options.engines === 'string' ? options.engines.split(',') : options.engines).map(i =>
            typeof i === 'string' ? i : i.id
        );
        if (enginesIds.length) {
            return this.engineUpdate.pipe(filter(id => enginesIds.includes(id))).subscribe(cb);
        } else {
            return Subscription.EMPTY;
        }
    }

    private getEngine(id: string) {
        const engine = RelatedProductsService.engines[id];
        if (!engine) {
            return undefined;
        }
        if (!engine.instance) {
            engine.instance = this.injector.get(engine.type);
            if (engine.instance!.subscribe) {
                this.subscriptions.add(engine.instance!.subscribe(() => this.engineUpdate.next(id)));
            }
        }
        return engine.instance;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    //////////////////////////////////////////////
    ///
    /// postprocesssing
    ///
    //////////////////////////////////////////////

    /**
     * Applies filters to product collection:
     * 1. out of stock items (if website does not show them)
     * 2. same family products (we show only one, unless product is divergent)
     * @param  {array} originalIds       Original products ids (ones we search relation to)
     * @param  {array} productIds        Related product ids
     * @param  {number[]} mainProductsParentIds Main product parent id
     */
    protected normalize(originalIds: number[], productIds: number[]): number[] {
        return normalizeProducts(originalIds, productIds);
    }

    /**
     * Skips discounted products
     */
    protected skipDiscounted(productIds: number[]): number[] {
        return this.app.resources.product
            .getAllExisting(productIds)
            .filter(model => !model.orgPrice)
            .map(model => model.id);
    }
}
